import React, { useEffect, useState, useRef } from "react";
import Input from "../ui/Input";
import { calculateProfitMargin, calculateSellPrice, centsToPrice, convertDiscountToInt, fillTemplate, kbdEvent, showConfirm } from "../utils/Utils";
import { useTabs } from "../hooks/useTabs";
import { getSignal } from "../App";
import PriceField from "../ui/PriceField";
import Button from "../ui/Button";
import { KBD } from "../utils/Keybindings";
import Space from "../ui/Space";
import Label from "../ui/Label";
import Checkbox from "../ui/Checkbox";
import { ROUTES } from "../utils/Routes";
import CustomersController from "../controllers/CustomersController";
import { createScreen } from "../hooks/createScreen";
import DateField from "../ui/DateField";
import { listen } from "../hooks/Listener";
import { showError, showMessage } from "../ui/Message";
import { pubCall } from "../utils/PubSub";
import { createModal } from "../hooks/createModal";
import InventoryPubstock from "./InventoryPubstock";
import DateInput from "../ui/DateInput";
import dayjs from "dayjs";




export const drawOrderHeaders = () => {
    return (
        <thead>
            <tr>
                <th style={{ "textAlign": "left", width: "45px" }} key="qty">Qty</th>
                <th style={{ "textAlign": "left", width: "75px" }} key="order">Order</th>
                <th style={{ "textAlign": "left", width: "34px" }} key="grp">Grp</th>
                <th style={{ "textAlign": "left", width: "74px" }} key="sup">SupPr</th>
                <th style={{ "textAlign": "left", width: "36px" }} key="disc">Disc</th>
                <th style={{ "textAlign": "left", width: "74px" }} key="sell">Sell</th>
                <th style={{ "textAlign": "left", width: "39px" }} key="pm">PM</th>
                <th style={{ "textAlign": "left", width: "20px" }} key="remove"></th>
                <th style={{ "textAlign": "left" }} key="customer"></th>
            </tr>
        </thead>
    )
}

export default function InventoryOrder(props) {

    const signal = getSignal();
    const session = signal?.session;
    const splitter = session?.is_splitter_active;
    const largestChainNum = Math.max(0, ...(session?.ms_info?.map(obj => obj.chain_num) ?? []));
    const stores = session?.ms_info.filter(item => item.is_splitter_active) ?? [];
    const myStoreIndex = stores.findIndex(item => item.san === session?.store_san);
    const full_split = useRef(new Array(largestChainNum + 1).fill(0));

    const [loading, setLoading] = useState(false);

    // Callbacks & Props
    const {
        controller = "",
        edit = true,
        route = "",
        onSave = () => { },
        onOpenFull = () => { },
        onRemove = () => { },
        updateSellPrice = () => { },
        onExit = () => { },
        onNew = () => { },
        onSelect = () => { },
        show_headers = false,
        selected = "",
        is_active = false,
        setSelected = () => { },
        customerRequired = false,
        full = true,
        pubstock = false,
        fullData = {},
        reserveFromOrder = false,
    } = props;

    const {
        sell_price = 0,
        exchange_rate = 1
    } = fullData?.stock || {};


    // Order object
    const {
        order = {
            cancel_date: null,
            clerk_code: "",
            condition_code: "",
            customer: null,
            customer_po: "",
            invoice: "",
            invoice_date: null,
            invoice_price: 0,
            is_single_title_order: false,
            ms_split: [],
            note: [],
            order_id: "",
            order_number: "",
            order_separator: "",
            po_number: "",
            primary_code: "",
            quantity: 0,
            review_days: 0,
            status: "",
            status_date: 0,
            supplier: "",
            supplier_discount: 0
        }
    } = props;



    const [open, setOpen] = useState(full);

    // Synthetic
    const store_profit_margin = calculateProfitMargin(sell_price, order.invoice_price, order.supplier_discount, exchange_rate)

    // Fields array to hold data and traverse
    const start_index = (full) ? "customer.name" : "quantity";


    const canFieldEdit = (_key) => {

        if (!full) {
            return true;
        }

        if (!edit) {
            return false;
        }

        if (!full) {
            return false;
        }

        if (order?.status === "Pend") {
            return true;
        }
        if (["cancel_date", "sell_price", "store_profit_margin", "note.0", "note.1", "note.2", "customer.name", "customer.communication_preference", "customer_po"].includes(_key)) {
            return true;
        }
        return false;
    }

    const fields = [
        { type: "row", key: "quantity", value: order.quantity, skip: !canFieldEdit("quantity") },
        { type: "row", key: "supplier", value: order.supplier, skip: !canFieldEdit("supplier") },
        { type: "row", key: "order_separator", value: order.order_separator, skip: !canFieldEdit("order_separator") },
        { type: "row", key: "invoice_price", value: order.invoice_price, skip: !canFieldEdit("invoice_price") },
        { type: "row", key: "supplier_discount", value: order.supplier_discount, skip: !canFieldEdit("supplier_discount") },
        { type: "row", key: "sell_price", value: sell_price, skip: !canFieldEdit("sell_price") }, // Sell price comes from title data... 
        { type: "row", key: "store_profit_margin", value: store_profit_margin, skip: !canFieldEdit("store_profit_margin") }, // Sell price comes from title data... 
        { type: "row", key: "remove", value: "", skip: (!order?.order_id || full) }, // Remove button
        { type: "row", key: "expand", value: "", skip: full }, // Open Button 
        // Full view
        { type: "row", key: "customer", value: order.customer, skip: true },
        { type: "row", key: "splitter", value: order.ms_split, skip: !splitter },
        { type: "full", key: "ms_split", value: order.ms_split, skip: true },
        { type: "full", key: "shop", value: order?.customer?.shop || "", skip: true },
        { type: "full", key: "ship_to", value: order?.customer?.ship_to || "", skip: true },
        { type: "full", key: "customer.name", value: order?.customer?.name || "" },
        { type: "full", key: "customer.communication_preference", skip: (!order?.customer), value: order?.customer?.communication_preference || "" },
        { type: "full", key: "customer_po", label: "CustPO", skip: (!order?.customer), value: order.customer_po },
        { type: "full", key: "note.0", autoselect: false, value: order?.notes?.[0] || "" },
        { type: "full", key: "note.1", autoselect: false, value: order?.notes?.[1] || "" },
        { type: "full", key: "note.2", autoselect: false, value: order?.notes?.[2] || "" },
        { type: "full", key: "is_single_title_order", type: "checkbox", value: order.is_single_title_order, skip: !canFieldEdit("is_single_title_order") },
        { type: "full", key: "is_prepay", type: "checkbox", value: order.is_prepay, skip: !canFieldEdit("is_prepay") },
        { type: "full", key: "is_direct_to_home", type: "checkbox", value: order.is_direct_to_home, skip: !canFieldEdit("is_direct_to_home") },
        { type: "full", key: "status", value: order.status, skip: !canFieldEdit("status") },
        { type: "full", key: "status_date", value: order.status_date || dayjs().valueOf(), skip: true },
        { type: "full", key: "review_days", value: order.review_days, skip: !canFieldEdit("review_days") },
        { type: "full", key: "review_date", value: (order.status_date) ? dayjs(order.status_date).add(order.review_days || 0, "days").format("YYYY-MM-DD HH:mm:ss") : dayjs().format("YYYY-MM-DD HH:mm:ss"), skip: !canFieldEdit("review_date"), skip: true },
    ]

    const splitterIndex = fields.findIndex(tab => tab.key === "ms_split");

    if (splitter && splitterIndex !== -1) {
        const storeTabs = stores.map((item, index) => ({
            splitter: true,
            key: item.san,
            type: "row"
        }));
        // insert right after the splitter
        fields.splice(splitterIndex + 1, 0, ...storeTabs);
    }

    const filtered_fields = (full) ? fields : (splitter) ? fields.filter(item => item.type === "row") : fields.filter(item => item.type === "row" && item.key !== "splitter")

    const { tabs, index, help, update, updateSkip, setIndex, next, prev, refs, getVal } = useTabs(filtered_fields, start_index);




    useEffect(() => {

        // Return if not in edit mode 
        if (!edit) {
            return;
        }

        if (refs?.current[index]) {
            refs.current[index].focus();
        }

        const actions = [
            { key: KBD.ESCAPE, action: exit },
            { key: KBD.TAB, action: next },
            { key: KBD.SHIFT_TAB, action: prev },
            { key: KBD.PAGE_DOWN, action: () => { saveOrder() } },
        ];

        if (full) {
            actions.push({ key: KBD.ARROW_DOWN, action: next });
            actions.push({ key: KBD.ARROW_UP, action: prev });
        }

        const cleanup = listen(controller, route, actions, () => { }, [
            KBD.ARROW_DOWN,
            KBD.ARROW_UP,
            KBD.TAB,
            KBD.SHIFT_TAB,
            KBD.ESCAPE,
            KBD.ENTER,
            KBD.PAGE_UP,
            KBD.PAGE_DOWN,
            KBD.CTRL_C,
            KBD.CTRL_V,
        ]);

        return cleanup;
    }, [index, tabs, edit]);


    function arrayToObject(arr) {
        return arr.reduce((obj, item) => {
            if (item.key) {
                obj[item.key] = item.hasOwnProperty("value") ? item.value : null;
            }
            return obj;
        }, {});
    }




    const changeSellPrice = (_sell_price) =>
        new Promise(resolve => {
            showConfirm({
                controller: controller,
                route,
                description: (
                    <div style={{ textAlign: "center", "width": "330px" }}>
                        You are changing the inventory price: from<strong> {centsToPrice(sell_price)}</strong> to <strong>{centsToPrice(_sell_price)}</strong>.
                        <div className="shim" /><div>Are you sure?</div><div className="shim" />
                    </div>
                ),
                title: "Inventory price change",
                callback: confirmed => {
                    if (confirmed) {
                        updateSellPrice(_sell_price)
                    } else {
                        // revert;
                        update("sell_price", sell_price);
                        update("store_profit_margin", calculateProfitMargin(sell_price, order.invoice_price, order.supplier_discount, 1))
                    }
                    resolve(true)
                }
            });
        });

    const saveOrder = async () => {



        requestAnimationFrame(() => { })
        // blur input


        // convert array to keyed object (need to convert to an order object later)
        const _update = arrayToObject(tabs);

        // Check for customer
        if (customerRequired && !_update?.customer) {
            showError({ error: "Customer is required" });
            return;
        }



        // if sell price is modified, update the title object (wait before continuing...)
        if (sell_price !== _update.sell_price) {
            const sell_price_updated = await changeSellPrice(_update?.sell_price);
            if (!sell_price_updated) return;
        }





        blurField();
        // Start order stuff
        setLoading(true);

        // Check quantity and remove if zero
        if (_update.quantity === 0) {
            if (order?.order_id) {
                removeOrder(order?.order_id);
            } else {
                focusField();
                showError({ error: "Quantity required." })
            }
            setLoading(false);
            return;
        }


        // Create the order object
        let _order = fillTemplate(order, _update);

        // Add or edit depending on ID


        let _request_obj = {
            order_item: _order,
            require_clerk_code: true
        }


        let _api = (_order?.order_id) ? "order.editItem" : "order.addItem";


        if (reserveFromOrder) {

            // Change API, add FROM, option to replenish

            _api = "order.reserveFromItem";
            _request_obj.new_order_item = _request_obj.order_item;

            showConfirm({
                controller: controller,
                route,
                description: (
                    <div style={{ textAlign: "center", "width": "330px" }}>
                        You are reserving against an existing stock order.
                        <div className="shim" /><div>Do you want to replenish the qty you are reserving with a Pending order?</div><div className="shim" />
                    </div>
                ),
                title: "Inventory replenish",
                callback: replenish => {

                    pubCall(_api, { ..._request_obj, "from_order_item_id": reserveFromOrder?.order_id, "replenish": replenish }, (_s, _r) => {
                        if (_s) {
                            showMessage("Order added.")
                            onSave(_r);
                            props.callback(true);
                        } else {
                            focusField();
                            showError(_r);
                        }
                        setLoading(false);
                    })
                }
            });


        } else {


            pubCall(_api, _request_obj, (_s, _r) => {
                if (_s) {
                    showMessage((_order?.order_id) ? "Order updated" : "Order added");

                    onSave(_r);
                    if (full) {
                        props.callback(true);
                    }
                } else {
                    focusField();
                    showError(_r)
                }
                setLoading(false);
            })

        }



    }

    const blurField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.blur === 'function') {
            el.blur();
        }
    }

    const focusField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.focus === 'function') {
            el.focus();
        }
    }


    const removeOrder = (_order_id) => {
        blurField();
        setLoading(true)
        pubCall("order.removeItem", { order_id: _order_id, require_clerk_code: true }, (_s, _r) => {
            if (_s) {
                showMessage("Order removed.");
                onRemove(_order_id);
            } else {
                focusField();
                showError(_r);
            }
            setLoading(false)
        })
    };

    const exit = () => {
        blurField();
        if (full) {
            props.callback(false);
        }
        onExit()
    }


    const editProps = (key) => {

        const tab = tabs.find(tab => tab.key === key) || {};
        let _edit = (full) ? canFieldEdit(key) : edit;



        return {
            onFocus: () => { setIndex(key) },
            ref: el => (refs.current[key] = el),
            edit: _edit,
            dataId: key,
            controlled: tab.controlled ?? true,
            autoselect: tab.autoselect ?? true,
            value: getVal(key),
            onChange: tab.onChange ?? (e => update(key, e)),
            onEnter: () => next()
        };
    };


    const updateSplit = (source, changedValue) => {


        const rawMsValues = getVal("ms_split");
        let ms_values = [...stores].map(() => 0);

        if (Array.isArray(rawMsValues) && rawMsValues.length === stores.length) {
            ms_values = [...rawMsValues];
        } else if (Array.isArray(rawMsValues) && rawMsValues.length > stores.length) {
            // Map to stores
            let _new = [];
            stores.forEach((store) => {
                _new.push(rawMsValues[store.chain_num]);
            });
            ms_values = _new;
        }

        const quantity = Number(getVal("quantity"));
        const numStores = stores.length;
        const primaryIndex = myStoreIndex;



        const setMsValuesAndQuantity = (newVals, overide_total = false) => {
            const total = newVals.reduce((a, b) => a + b, 0);


            // keep full_split in sync
            stores.forEach((store, index) => {
                if (full_split.current) {
                    full_split.current[store.chain_num] = newVals[index];
                }
            });

            // build updates for useTabs
            const updates = {
                ms_split: newVals,
                splitter: "",
                quantity: overide_total !== false ? overide_total : total,
            };
            stores.forEach((store, index) => {
                updates[store.san] = newVals[index];
            });


            // update both tabs + current
            update(updates);


        };



        if (source === "splitter") {
            const splitter = String(changedValue).trim();

            if (!splitter.includes(".")) {
                if (!isNaN(splitter) && parseInt(splitter) > 0) {
                    return updateSplit("quantity", changedValue);
                } else {
                    return updateSplit("quantity", getVal("quantity"));
                }
            }

            if (splitter === ".") {
                const even = Math.floor(quantity / numStores);
                const remainder = quantity % numStores;
                const newVals = ms_values.map((_, i) => i < remainder ? even + 1 : even);
                return setMsValuesAndQuantity(newVals);
            }

            if (splitter.startsWith(".")) {
                const q = Number(splitter.slice(1));
                if (isNaN(q)) return;
                const newVals = stores.map(() => q);
                return setMsValuesAndQuantity(newVals);
            }

            const match = splitter.match(/^(\d+)\.(\d+)$/);
            if (match) {
                const chainNum = parseInt(match[1]);
                const val = parseInt(match[2]);

                const storeIndex = stores.findIndex(s => s.chain_num === chainNum);
                if (storeIndex === -1) return;

                const newVals = [...ms_values];
                newVals[storeIndex] = val;

                return setMsValuesAndQuantity(newVals);
            }

            return;
        }

        if (source === "quantity") {

            const newQuantity = Number(changedValue);
            const existingQuantity = Number(getVal("quantity"));
            if (newQuantity === existingQuantity) {
                return;
            }

            const currentTotal = ms_values.reduce((a, b) => a + b, 0);
            const delta = newQuantity - currentTotal;

            const newVals = [...ms_values];

            if (delta > 0) {
                newVals[primaryIndex] += delta;
            } else if (delta < 0) {
                let currentTotal = newVals.reduce((a, b) => a + b, 0);
                while (currentTotal > newQuantity) {
                    let maxIndex = 0;
                    for (let i = 1; i < newVals.length; i++) {
                        if (newVals[i] >= newVals[maxIndex]) {
                            maxIndex = i;
                        }
                    }
                    newVals[maxIndex] -= 1;
                    currentTotal -= 1;
                }
            }

            setMsValuesAndQuantity(newVals);
            return;
        }

        if (typeof source === "number") {
            const idx = source;
            const inputVal = Number(changedValue);
            if (isNaN(inputVal)) return;

            if (inputVal === ms_values[idx]) {
                return;
            }

            const oldVal = ms_values[idx];
            const delta = inputVal - oldVal;
            const newVals = [...ms_values];
            newVals[idx] = inputVal;

            if (idx === primaryIndex) {
                const newTotal = newVals.reduce((a, b) => a + b, 0);
                setMsValuesAndQuantity(newVals, newTotal);
            } else {
                const primaryVal = newVals[primaryIndex];
                const reduction = Math.min(primaryVal, delta);
                newVals[primaryIndex] = primaryVal - reduction;

                const newTotal = newVals.reduce((a, b) => a + b, 0);
                setMsValuesAndQuantity(newVals, newTotal);
            }

            return;
        }
    };


    const openFull = () => {
        blurField();
        window.bm_state.value = ROUTES.INVENTORY.ORDER

        const _u = arrayToObject(tabs);
        const _order = fillTemplate(order, _u);

        createModal({
            component: <InventoryOrder
                route={ROUTES.INVENTORY.ORDER}
                updateSellPrice={updateSellPrice}
                onSave={(e) => {
                    props?.onSave(e);
                }}
                onExit={() => {
                    window.bm_state.value = route;
                    setIndex("quantity")
                }}
                onRemove={() => { }}
                fullData={props?.fullData || {}}
                controller={controller}
                order={_order}
                full={true}
                sell_price={sell_price}
                edit={true}
            />, closable: true, title: "Order",
            callback: (e) => {
                if (!e) {
                    window.bm_state.value = route;
                    setIndex("quantity")
                }


            }
        });

    }


    const columns = [

        {
            key: "quantity", width: "50px", label: "Qty", render: () => {

                if (splitter) {
                    return (
                        <Input
                            {...editProps("quantity")}
                            onEnter={(e) => { updateSplit("quantity", e); next() }}
                            onChange={(e) => updateSplit("quantity", e)}
                            width={33}
                            min
                            max={(reserveFromOrder) ? reserveFromOrder?.quantity : 999}
                            className={(edit) ? "text-orange-bg" : "text-orange-bg text-black"}
                            number
                            right
                            style={{ margin: "0px 1px" }}
                        />
                    )
                }

                return (
                    <Input
                        {...editProps("quantity")}
                        width={33}
                        min
                        className={(edit) ? "text-orange-bg" : "text-orange-bg text-black"}
                        number
                        style={{ margin: "0px 1px" }}
                        right
                    />
                )
            }
        },
        {
            key: "supplier", width: "82px", label: <><span style={{ "color": "var(--text)" }}>&nbsp;Orders</span></>, render: (e, f) => {
                return (
                    <div style={{ "width": "77px", backgroundColor: "#1e1e1e" }}>
                        <Input
                            placeholder="Supp..."
                            {...editProps((!order?.order_id && !edit) ? "" : "supplier")}
                            dataId="supplier"
                            width={65}
                            onEnter={(e) => {
                                if (pubstock && !f?.order_id) {
                                    const found = fullData?.pubstock.find(item =>
                                        item.supplier.slice(0, 3).toLowerCase() === e.slice(0, 3).toLowerCase()
                                    );
                                    if (found) {
                                        // update supplier
                                        updateFromPubstock(found);
                                        next();
                                    } else {
                                         update("supplier", e);
                                         next();
                                    }
                                } else {
                                    update("supplier", e);
                                    next(); 
                                }
                            }}
                            maxLength={5}
                            uppercase
                            style={{ margin: "0px 1px" }}
                        />
                    </div>
                )
            }
        },
        {
            key: "order_separator", width: "38px", label: "Grp", render: () => {
                return (
                    <Input
                        {...editProps("order_separator")}
                        alpha
                        width={20}
                        maxLength={1}
                        style={{ margin: "0px 1px" }}
                        uppercase
                    />
                )
            }
        },


        {
            key: "invoice_price", width: "78px", label: "SupPr", render: () => {

                return (
                    <PriceField
                        {...editProps("invoice_price")}
                        onChange={(e) => {
                            update({
                                invoice_price: e,
                                store_profit_margin: calculateProfitMargin(getVal("sell_price"), e, getVal("supplier_discount"), exchange_rate)
                            })
                        }}
                        width={60}
                        min
                        price
                        style={{ margin: "0px 1px" }}
                        right
                    />
                )
            }
        },
        {
            key: "supplier_discount", width: "40px", label: "Disc", render: () => {
                return (
                    <Input
                        {...editProps("supplier_discount")}
                        onChange={(e) => {
                            update({
                                supplier_discount: e,
                                store_profit_margin: calculateProfitMargin(getVal("sell_price"), getVal("invoice_price"), e, exchange_rate)
                            })
                        }}
                        width={22}
                        maxLength={2}
                        number
                        style={{ margin: "0px 1px" }}
                        right
                    />
                )
            }
        },

        {
            key: "sell_price", width: "78px", label: "Sell", render: () => {

                return (
                    <PriceField
                        {...editProps("sell_price")}
                        onChange={(e) => {
                            update({
                                sell_price: e,
                                store_profit_margin: calculateProfitMargin(e, getVal("invoice_price"), getVal("supplier_discount"), 1)
                            })
                        }}
                        width={60}
                        min
                        price
                        right
                        style={{ margin: "0px 1px" }}
                    />
                )
            }
        },
        {
            key: "store_profit_margin", width: "43px", label: "PM", render: () => {


                return (
                    <Input
                        {...editProps("store_profit_margin")}
                        onChange={(e) => {
                            update({
                                store_profit_margin: e,
                                sell_price: calculateSellPrice(getVal("invoice_priec"), getVal("supplier_discount"), e, 1)
                            })
                        }}
                        onEnter={() => { if (open) { next() } else { kbdEvent(KBD.PAGE_DOWN) } }}
                        width={25}
                        min
                        right
                        number
                        maxLength={2}
                        style={{ margin: "0px 1px" }}
                    />
                )
            }
        },

        {
            key: "remove", width: "25px", label: <></>, render: (e, f) => {

                
                if(!edit || !order?.order_id){
                    return <></>; 
                }

                return (
                    <div><Button disabled={(!order?.order_id || !edit || full)} style={{ margin: "0px 1px" }} className={"ui-tbl-btn"} {...{
                        ...editProps("remove"), onClick: () => {

                            if (window.bm_state.value !== route) {
                                kbdEvent(KBD.O);
                                return;
                            }

                            if (order?.order_id) {
                                removeOrder(order?.order_id)
                            }

                        }
                    }}>X</Button></div>
                )
            }
        },

        {
            key: "expand", label: <div style={{ "textAlign": "right", "pointerEvents": "all" }}><Button disabled={(order?.id)} onClick={() => kbdEvent(KBD.O)} type="link">New+</Button></div>, render: () => {
                return (
                    <div style={{ "textAlign": "right", "width": "115px" }}><Button disabled={(full || !edit)} {...{
                        ...editProps("expand"), onClick: () => {

                            openFull()

                        }
                    }} onEnter={() => openFull()} type="link"><nobr>{(order?.customer) ? "S/O" : " +Cust/Note"}</nobr></Button>

                    </div>
                )
            }
        },

    ];





    const selectCustomer = (_search = "") => {
        window.bm_state.value = ROUTES.CUSTOMER.CUSTOMER;
        blurField("customer.name");
        createScreen({
            signal: signal,
            component: <CustomersController controller={controller} is_modal search={_search} route={ROUTES.CUSTOMER.CUSTOMER} />, closable: false, title: "Select Customer",
            callback: (_obj) => {
                window.bm_state.value = route;
                if (_obj) {
                    update({ ["customer.name"]: _obj.name, customer: _obj, ["customer.communication_preference"]: _obj.communication_preference, "ship_to": _obj.ship_to, "shop": _obj.shop })
                    updateSkip({ ["customer.communication_preference"]: false, customer_po: false })
                }
                refs.current["customer.name"].focus();
                setTimeout(() => {
                    kbdEvent(KBD.TAB);
                }, 100)

            }
        });
    }


    const drawRow = () => {

        if (!full) {
            return columns.map((col, index) => (
                <div key={col.key} style={{ overflow: "hidden", "height": "32px", width: col?.width, "borderRight": (full || index === columns?.length - 1) ? "none" : "1px solid var(--border-secondary)" }}>
                    <div style={{ "paddingTop": "4px" }}>{(col?.render && col.render())}</div>
                </div>
            ))
        }
        return (
            <tr>
                {columns.map((col) => (
                    <td style={{ "height": "auto" }} key={col.key}>
                        <div style={{ overflow: "hidden", "height": "32px" }}>
                            <div style={{ "paddingTop": "4px" }}>{(col?.render && col.render())}</div>
                        </div>
                    </td>
                ))}
            </tr>
        )
    }


    const drawSplitter = (order) => {
        if (!splitter || order?.customer) {
            return;
        }
        return (
            <tr>
                <td colSpan={9}>
                    <div style={{ "borderTop": (full) ? '1px solid var(--border-secondary)' : "none", "width": "maxContent", "borderRight": "1px solid var(--border-secondary)", "borderLeft": "1px solid var(--border-secondary)", "margin": "0px -6px", "padding": "0px 0px" }}>
                        <table style={{ "backgroundColor": "var(--card-bg)" }} className="ui-table">
                            <thead>
                                <tr key={"splitter"}>
                                    <th style={{ "textAlign": "left" }}>Splitter</th>
                                    {stores.map((store) => (
                                        <th style={{ "textAlign": "right" }} key={store.san}>#{store.chain_num}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr key={"splitter_input"}>
                                    <td><Input chars={["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "."]} {...editProps("splitter")} onEnter={(e) => { updateSplit("splitter", e); next() }} onBlur={(e) => updateSplit("splitter", e)} width={68} /></td>
                                    {stores.map((store, index) => {
                                        const _ms = getVal("ms_split") || [6];
                                        const _value = getVal(store.san) || (_ms[store.chain_num] || 0)
                                        return (<td style={{ "textAlign": "right" }} key={store.san}><Input {...editProps(store.san)} onEnter={(e) => {
                                            updateSplit(index, e);
                                            if (index === store.length - 1) {
                                                // last field save
                                                kbdEvent(KBD.PAGE_DOWN)
                                            } else {
                                                kbdEvent(KBD.TAB)
                                            }
                                        }} onChange={(e) => updateSplit(index, e)} number width={25} value={_value} /></td>)
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </td>
            </tr>
        )
    }

    const handleReviewChange = (field, value) => {
        const statusDate = getVal("status_date")
            ? dayjs(getVal("status_date"))
            : dayjs(); // fallback to today

        if (field === "review_days") {
            const days = Number(value) || 0;

            update("review_days", days);
            update(
                "review_date",
                statusDate.add(days, "day").format("")
            );
        }

        if (field === "review_date") {
            const reviewDate = dayjs(value);
            const days = reviewDate.diff(statusDate, "day");

            update("review_date", reviewDate.valueOf());
            update("review_days", days);
        }
    };


    const drawDetails = () => {

        return (
            <tr>
                <td colSpan={9}>
                    <div style={{ "width": "525px" }}>
                        <div className="shim" /><div className="shim" />
                        <Space grow>
                            <Label style={{ "width": "39px" }}>Cust</Label>
                            <Input {...editProps("customer.name")} onEnter={(e) => selectCustomer(e)} style={{ width: "144px" }} />
                            <Label>Act#</Label>
                            <Label width={100} style={{ "color": "var(--text-light)", "textAlign": "left" }}>{getVal("ship_to")}</Label>
                            <Label>Type</Label>
                            <Label width={30} style={{ "color": "var(--text-light)", "textAlign": "left" }}>{getVal("shop")}</Label>
                        </Space>
                        <div className="shim" /><div className="shim" />
                        <Space grow>
                            <Label style={{ "width": "39px" }}>Notify</Label>
                            <Input {...editProps("customer.communication_preference")} edit={getVal("customer")} className={(getVal("customer")) ? "" : "dim"} uppercase maxLength={1} width={20} />
                            <Label width={218}>PO#</Label>
                            <Input {...editProps("customer_po")} edit={getVal("customer")} className={(getVal("customer")) ? "" : "dim"} width={107} />
                        </Space>
                        <div className="shim" /><div className="shim" />
                        <Space style={{ "alignItems": "flex-start" }} grow>
                            <Label style={{ "width": "39px" }}>Notes</Label>
                            <Space size={0} flexDirection="column">
                                <Input style={{ "borderBottomLeftRadius": "0px", "borderBottomRightRadius": "0px", "fontFamily": "monospace", "fontSize": "17px" }} width={368} maxLength={38} {...editProps("note.0")} tabMax={() => refs.current[`note.1`].focus()} autoselect={false} />
                                <Input style={{ "borderRadius": "0px", "fontFamily": "monospace", "fontSize": "17px" }} width={368} maxLength={38} {...editProps("note.1")} tabMin={() => refs.current[`note.0`].focus()} tabMax={() => { refs.current[`note.2`].focus(); }} autoselect={false} />
                                <Input style={{ "borderTopLeftRadius": "0px", "borderTopRightRadius": "0px", "fontFamily": "monospace", "fontSize": "17px" }} width={368} maxLength={38} {...editProps("note.2")} tabMin={() => refs.current[`note.1`].focus()} autoselect={false} />
                            </Space>
                        </Space>
                        <div className="shim" /><div className="shim" />
                        <Space grow>
                            <Label style={{ "width": "39px" }}>&nbsp;</Label>
                            <Space size={40}>
                                <Checkbox {...editProps("is_single_title_order")} onEnter={() => next()}><small>Single Title Order</small></Checkbox>
                                <Checkbox {...editProps("is_prepay")} onEnter={() => next()}><small>Prepay</small></Checkbox>
                                <Checkbox {...editProps("is_direct_to_home")} onEnter={() => next()}><small>Direct to home</small></Checkbox>
                            </Space>
                        </Space>
                        <div className="shim" /><div className="shim" />
                        <Space  >
                            <Label style={{ "width": "39px" }}>Stat</Label>
                            <Input {...editProps("status")} width={53} />
                            <DateInput {...editProps("status_date")} value={dayjs(getVal("status_date")).format("YYYY-MM-DD HH:mm:ss")} edit={false} width={89} />
                            <Label width={50}>Review</Label>
                            <Input number maxLength={3} {...editProps("review_days")} onChange={(e) => {
                                handleReviewChange("review_days", e);
                            }
                            } onEnter={() => kbdEvent(KBD.PAGE_DOWN)} right width={33} />
                            <DateInput {...editProps("review_date")} onEnter={() => kbdEvent(KBD.PAGE_DOWN)} edit={false} onChange={(_v) =>
                                handleReviewChange("review_date", _v)
                            }
                                maxLength={10} width={89} />
                        </Space>
                        <div className="shim" /><div className="shim" />
                    </div>
                </td>
            </tr>
        )
    }


    const updateFromPubstock = (_pubstock) => {
        let _t = {};
        _t.supplier = _pubstock?.supplier && _pubstock?.supplier.length === 5 ? _pubstock?.supplier.slice(0, 3) : _pubstock?.supplier;
        _t.invoice_price = parseFloat(_pubstock.supplier_price);
        _t.supplier_discount = convertDiscountToInt(_pubstock.discount);

        // Update price if no TOH
        if (!fullData?.stock?.total_onhand) {
            _t.sell_price = parseFloat(_pubstock?.sell_price);
        }

        update(_t);
    }

    const drawPubstock = () => {

        return (
            <div>
                <div style={{ "borderTop": '1px solid var(--border-secondary)' }}>
                    <InventoryPubstock
                        onSelectSupplier={(_pubstock) => {
                            updateFromPubstock(_pubstock)
                        }}
                        can_toggle={false}
                        controller={controller}
                        is_order
                        pending_orders={fullData.pending_orders}
                        pubstock={fullData?.pubstock}
                    />
                </div>
            </div>

        )
    }

    // If not full, only draw the row
    if (!full) {
        return (
            <div onClick={(e) => {
                if (is_active) {
                    const el = e.target.closest('[data-id]');
                    if (el?.dataset?.id) {
                        setIndex(el.dataset.id);
                    }
                }
            }} onDoubleClick={(e) => {
                if (!is_active) {
                    const el = e.target.closest('[data-id]');
                    if (el?.dataset?.id) {
                        setIndex(el.dataset.id);
                    }
                    window.bm_state.value = ROUTES.INVENTORY.ORDERS;
                    setSelected(props?.order);
                }
            }}>
                <div className="fade" onClick={() => {
                    if (!is_active) {
                        return;
                    }
                    onSelect()
                }}>

                    <div style={{ "height": "35px", "backgroundColor": "var(--card-bg)", "padding": "0px 3px", "borderTop": "0px solid var(--border-secondary)" }}>
                        <Space>{drawRow()}</Space>
                    </div>
                    <div style={{ "height": "56px", "marginTop": "-3px", "backgroundColor": "var(--card-bg)", "padding": "0px 3px", "borderTop": "1px solid var(--border-secondary)" }}>
                        {drawSplitter()}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div style={{ "margin": "10px 0px", "marginBottom": "0px", "border": "1px solid var(--border-secondary)", "minWidth": "610px" }}>
                <table style={{ width: "100%", backgroundColor: "var(--card-bg)" }} className={"ui-table stretch-last"}>
                    {drawOrderHeaders()}
                    {drawRow()}
                    {drawSplitter()}
                    {drawDetails()}
                </table>
                {drawPubstock()}
            </div>

        </>)
}